import { t } from '@/i18n';
import { DayjsSchema, jsonType, sortOrderType } from '@/utils';
import { type ZodParsedType, z } from 'zod';

/// ////////////////////////////////////////////
/// enum
/// ////////////////////////////////////////////

export const treeNodeType = z.enum(['activity', 'indicator']);
export const TreeNodeTypeLabelModifier = {
  activity: t('activity'),
  indicator: t('indicator'),
} as const satisfies Record<z.infer<typeof treeNodeType>, string>;

export const indicatorType = z.enum(['output', 'outcome']);
export type IndicatorType = z.infer<typeof indicatorType>;
export const IndicatorTypeLabelModifier = {
  output: t('indicator-type-output'),
  outcome: t('indicator-type-outcome'),
} as const satisfies Record<IndicatorType, string>;

export const termType = z.enum(['short', 'medium', 'long']);
export type TermType = z.infer<typeof termType>;
export const TermTypeLabelModifier = {
  short: t('short-term'),
  medium: t('medium-term'),
  long: t('long-term'),
} as const satisfies Record<TermType, string>;

export const evaluationDirectionType = z.enum(['upward', 'downward']);
export type EvaluationDirectionType = z.infer<typeof evaluationDirectionType>;
export const EvaluationDirectionTypeLabelModifier = {
  upward: t('upward'),
  downward: t('downward'),
} as const satisfies Record<EvaluationDirectionType, string>;

export const dataNatureType = z.enum(['quantitative', 'qualitative']);
export const DataNatureTypeLabelModifier = {
  qualitative: t('qualitative'),
  quantitative: t('quantitative'),
} as const satisfies Record<z.infer<typeof dataNatureType>, string>;

/// ////////////////////////////////////////////
/// Model
/// ////////////////////////////////////////////

export const IndicatorModel = z.object({
  id: z.string().uuid(),
  createdAt: z.string(),
  updatedAt: z.string(),
  priorId: z.string().uuid().nullable(),
  levelByPrior: z.number().int(),
  upperId: z.string().uuid().nullish(),
  levelByUpper: z.number().int(),
  title: z.string().min(1),
  key: z.string().nullable(),
  json: jsonType.nullable(),
  fiscalYear: z.number().int(),
  type: indicatorType,
  description: z.string().nullish(),
  targetValue: z.number().nullable(),
  actualValue: z.number().nullish(),
  achievementRate: z.number().nullable(),
  unit: z.string().max(20).nullable(),
  evaluationDirection: evaluationDirectionType.nullish(),
  reason: z.string().nullish(),
  noTypeReason: z.string().nullish(),
  term: termType.nullable(),
  sortOrder: sortOrderType.nullable(),
  dataNatureType: dataNatureType.nullable(),
  statusOfAchievementWhenQualitative: z.string().nullable(),
  isTargetYear: z.boolean(),
});

export const IndicatorsModel = IndicatorModel.array();

const ActivityBaseModel = z.object({
  id: z.string().uuid(),
  createdAt: z.string(),
  updatedAt: z.string(),
  sortOrder: sortOrderType,
  activityContent: z.string().min(1),
  projectId: z.string().uuid(),
});

export const ActivityModel = ActivityBaseModel;
export const ActivitiesModel = ActivityBaseModel.array();

const ActivityNodePositionModel = z.object({
  x: z.number(),
  y: z.number(),
});

export const ActivityFlowNodeModel = z.object({
  id: z.string().uuid(),
  createdAt: z.string(),
  updatedAt: z.string(),
  position: ActivityNodePositionModel,
  activityId: z.string().uuid().nullable(),
  activity: ActivityModel.nullable(),
  indicatorId: z.string().uuid().nullable(),
  indicator: IndicatorModel.nullable(),
  projectId: z.string().uuid(),
});

export const ActivityFlowEdgeModel = z.object({
  id: z.string().uuid(),
  createdAt: z.string(),
  updatedAt: z.string(),

  // This means ActivityNode's id!
  // Not an activity, or indicator id!
  sourceNodeId: z.string().uuid(),
  targetNodeId: z.string().uuid(),

  label: z.string().nullable(),
  reasonWhyOutcomesCannotBeSetAtMultipleLevels: z.string().nullable(),
  projectId: z.string(),
});

export const ActivityFlowModel = z.object({
  nodes: ActivityFlowNodeModel.array(),
  edges: ActivityFlowEdgeModel.array(),
});

/// ////////////////////////////////////////////
/// Schema
/// ////////////////////////////////////////////

export const IndicatorMutationParamsSchema = IndicatorModel.pick({
  title: true,
  upperId: true,
  type: true,
  reason: true,
  noTypeReason: true,
  sortOrder: true,
  evaluationDirection: true,
})
  .extend({
    fiscalYear: DayjsSchema(),
    description: z.string(),
  })
  .merge(
    IndicatorModel.pick({
      priorId: true,
      targetValue: true,
      term: true,
      unit: true,
      dataNatureType: true,
    }).partial(),
  );

export const IndicatorFormSchema =
  IndicatorMutationParamsSchema.partial().superRefine((data, ctx) => {
    const addInvalidTypeIssue = (
      path: keyof typeof data,
      expected: ZodParsedType = 'string',
    ) => {
      if (!data[path]) {
        ctx.addIssue({
          code: z.ZodIssueCode.invalid_type,
          path: [path],
          expected,
          received: data[path] === null ? 'null' : 'undefined',
        });
      }
    };

    const addTooSmallIssue = (path: keyof typeof data) => {
      if (!data[path]) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_small,
          path: [path],
          minimum: 1,
          type: 'string',
          inclusive: true,
        });
      }
    };

    addInvalidTypeIssue('sortOrder', 'number');
    addInvalidTypeIssue('type');
    addTooSmallIssue('title');
    addTooSmallIssue('description');

    if (data.type === 'outcome') {
      addInvalidTypeIssue('term');
      addInvalidTypeIssue('dataNatureType');

      if (data.dataNatureType === 'qualitative') {
        addTooSmallIssue('noTypeReason');
      }
      if (data.dataNatureType === 'quantitative') {
        addInvalidTypeIssue('unit');
        addInvalidTypeIssue('evaluationDirection');
        addInvalidTypeIssue('reason');
      }
    }

    if (data.type === 'output') {
      addInvalidTypeIssue('unit');
    }
  });

export const UpdateConsecutiveIndicatorFormSchema = IndicatorModel.pick({
  title: true,
  type: true,
  term: true,
  unit: true,
  dataNatureType: true,
  statusOfAchievementWhenQualitative: true,
}).extend({
  annualValues: IndicatorModel.pick({
    targetValue: true,
    actualValue: true,
    achievementRate: true,
  })
    .extend({
      id: z.string().uuid().optional(),
      year: z.number().int(),
      isManualAchievementRate: z.boolean().optional(),
    })
    .array(),
});

export const UpdateConsecutiveIndicatorRequestSchema =
  UpdateConsecutiveIndicatorFormSchema.extend({
    baseIndicatorId: z.string().uuid().optional(),
  });

export const ActivityFormSchema = ActivityBaseModel.pick({
  activityContent: true,
  sortOrder: true,
});

export const CreateActivityParamsSchema = ActivityFormSchema.extend({
  projectId: z.string().uuid(),
});

export const UpdateActivityParamsSchema = ActivityFormSchema.extend({
  projectId: z.string().uuid(),
  id: z.string(),
});

export const ActivityTreeNodeFormSchema = z.object({
  nodeType: treeNodeType,
  sourceActivityId: z.string().uuid().nullish(),
  sourceIndicatorId: z.string().uuid().nullish(),
  targetIndicatorId: z.string().uuid(),
  label: z.string().nullish(),
  reasonWhyOutcomesCannotBeSetAtMultipleLevels: z.string().nullish(),
});

export const ActivityTreeNodeFormValidationSchema = z.discriminatedUnion(
  'nodeType',
  [
    ActivityTreeNodeFormSchema.extend({
      nodeType: z.literal('activity'),
      sourceActivityId: z.string().uuid(),
      sourceIndicatorId: z.string().uuid().nullish(),
    }),
    ActivityTreeNodeFormSchema.extend({
      nodeType: z.literal('indicator'),
      sourceActivityId: z.string().uuid().nullish(),
      sourceIndicatorId: z.string().uuid(),
    }),
  ],
);

export const UpdateActivityTreeSchema = z.object({
  projectId: z.string().uuid(),
  nodes: ActivityFlowNodeModel.pick({ id: true, position: true }).array(),
  edges: ActivityFlowEdgeModel.pick({
    id: true,
    sourceNodeId: true,
    targetNodeId: true,
    label: true,
  }).array(),
});

export const CreateTreeNodeRequestSchema = z.object({
  projectId: z.string().uuid(),
  sourceObject: z.object({
    activityId: z.string().uuid().nullable(),
    indicatorId: z.string().uuid().nullable(),
  }),
  targetObject: z.object({
    activityId: z.null(),
    indicatorId: z.string().uuid(),
  }),
  label: z.string().nullable(),
  reasonWhyOutcomesCannotBeSetAtMultipleLevels: z.string().nullable(),
});

export const DeleteTreeEdgeRequestSchema = z.object({
  projectId: z.string().uuid(),
  id: z.string().uuid(),
});

export const UpdateTreeEdgeRequestSchema = z.object({
  projectId: z.string().uuid(),
  id: z.string().uuid(),
  label: z.string().nullable(),
  reasonWhyOutcomesCannotBeSetAtMultipleLevels: z.string().nullable(),
});
